import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { TenantsTable } from "./tenants-table/TenantsTable";
import ApplicationLayout from "components/layout/ApplicationLayout";
import ManageLicenseAlertView from "components/licenses/license-alerts/manage-license-alerts/ManageLicenseAlertView";
import { ErrorModal } from "components/login/ErrorModal";
import { VideoTutorialButton } from "components/modal/video-modal/video-tutorial-button/VideoTutorialButton";
import { VideoModal } from "components/modal/video-modal/VideoModal";
import { TENANTS_ROUTE } from "components/router/Routes";
import TenantCreationAction from "components/tenants/add-tenant/TenantCreationAction";
import { AUTH_TENANT_CREATE } from "domain/authority";
import { LicenseData } from "domain/licenses";
import { calculateTrialDaysLeft, ContractType } from "domain/tenants";
import { TutorialVideoLink } from "domain/tutorialVideos";
import { fetchCurrentTenantRateVersions, licenseService, ProductToRateList } from "services/licenses/LicenseService";
import { deriveTenantUuid, getCurrentTenantDetails } from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";
import { userSessionService } from "services/user/UserSessionService";
import { formatDateWithoutTime, toUtcEndOfDay } from "utils/format";

type FetchState = "PENDING" | "FETCHED" | "FAILED";

export default function AllTenantsView(): JSX.Element {
    const { t } = useTranslation();
    const currentTenantDetails = getCurrentTenantDetails();
    const [state, setState] = useState({ count: 0 });
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [tenantLicenses, setTenantLicenses] = React.useState<LicenseData[] | undefined>();
    const [tenantLevel, setTenantLevel] = React.useState(0);
    const [tenantExpirationDate, setTenantExpirationDate] = React.useState<Date | undefined>();
    const [fetched, setFetched] = React.useState<FetchState>("PENDING");
    const [errorModalVisible, setErrorModalVisible] = React.useState(false);
    const [refreshingCount, setRefreshingCount] = React.useState(0);
    const [alertsViewVisibility, setAlertsViewVisibility] = React.useState<boolean>(false);
    const [parentDrawBackPrevention, setParentDrawBackPrevention] = React.useState<boolean>(false);
    const [parentContractType, setParentContractType] = React.useState<ContractType>(currentTenantDetails.contractType);
    const [parentTrialDaysLeft, setParentTrialDaysLeft] = React.useState<number | null>(14);
    const [tenantUuid, setTenantUuid] = React.useState<string>("");
    const [tenantName, setTenantName] = React.useState<string>("");
    const TENANT_MAX_LEVEL = 5;
    const [rateVersions, setRateVersions] = React.useState<ProductToRateList[]>([]);
    const [showVideoModal, setShowVideoModal] = React.useState(false);
    const watchTutorialButton = <VideoTutorialButton onClick={() => setShowVideoModal(true)} />;

    function onRefreshing() {
        setRefreshingCount((prevState) => prevState + 1);
    }

    function updateState() {
        return setState((prevState) => ({ count: prevState.count + 1 }));
    }

    const fetchTenantData = async () => {
        const expirationDateAbortController = new AbortController();
        abortControllers.push(expirationDateAbortController);
        const tenantPromise = tenantService.fetchTenant(deriveTenantUuid(), expirationDateAbortController);

        const licensesAbortController = new AbortController();
        abortControllers.push(licensesAbortController);
        const licensePromise = licenseService.fetchLicenses(licensesAbortController);
        await Promise.all([tenantPromise, licensePromise])
            .then(([tenant, licenses]) => {
                // Typing for tenant.expirationDate says it can't be null or undefined but it comes from the backend
                // so we're prepared for a non-string.
                if (tenant.expirationDate) {
                    const tenantExpiration = new Date(
                        toUtcEndOfDay(new Date(formatDateWithoutTime(tenant.expirationDate)))
                    );
                    setTenantExpirationDate(tenantExpiration);
                }
                setParentContractType(tenant.contractType);
                setParentDrawBackPrevention(tenant.parentDrawbackPrevention);
                setTenantLevel(tenant.tenantHierarchyLevel);
                setTenantLicenses(licenses.licenses);
                setParentTrialDaysLeft(calculateTrialDaysLeft(tenant));
                setFetched("FETCHED");
            })
            .catch(() => {
                setFetched("FAILED");
                setErrorModalVisible(true);
            });
    };

    React.useEffect(() => {
        fetchTenantData().then(() =>
            abortControllers.filter((value) => !value.signal.aborted).forEach((value) => value.abort())
        );

        fetchCurrentTenantRateVersions().then((fetchedRateVersions) => {
            setRateVersions(fetchedRateVersions);
        });
    }, []);

    function createViewActions(): JSX.Element | undefined {
        if (
            !userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") ||
            !userSessionService.userHasAllAuthorities([AUTH_TENANT_CREATE])
        ) {
            return undefined;
        }
        if (fetched === "FETCHED" && tenantLevel < TENANT_MAX_LEVEL) {
            return (
                <TenantCreationAction
                    updateState={() => updateState()}
                    tenantExpirationDate={tenantExpirationDate}
                    tenantLevel={tenantLevel}
                    tenantLicenses={tenantLicenses}
                    rateVersions={rateVersions}
                    tenantUuid={deriveTenantUuid()}
                    parentDrawbackPrevention={parentDrawBackPrevention}
                    parentContractType={parentContractType}
                    trialDaysLeft={parentTrialDaysLeft}
                />
            );
        }
        if (fetched === "FAILED" || tenantLevel >= TENANT_MAX_LEVEL) {
            return undefined;
        }
        return;
    }

    const viewTenants = (): boolean => {
        return !alertsViewVisibility;
    };

    return (
        <>
            <ApplicationLayout
                viewTitle={viewTenants() ? t("Common.customerTitle") : undefined}
                view={
                    viewTenants() ? (
                        <TenantsTable
                            count={state.count}
                            onTenantEdit={updateState}
                            onTenantDelete={updateState}
                            refreshCount={refreshingCount}
                            parentLevel={tenantLevel + 1}
                            setNewComponent={setAlertsViewVisibility}
                            tenantUuid={setTenantUuid}
                            tenantName={setTenantName}
                            rateVersions={rateVersions}
                        />
                    ) : (
                        <ManageLicenseAlertView
                            hideDialogView={setAlertsViewVisibility}
                            initialTenantData={undefined}
                            setEditView={() => {
                                ("");
                            }}
                            edit={false}
                            alertData={{
                                alertUuid: "",
                                alertName: "",
                                tenantName: "",
                                tenantUuid: "",
                                availableLicenses: [],
                                expirationDate: "",
                                poolName: "",
                                poolUuid: "",
                            }}
                            tenantsView={true}
                            tenantName={tenantName}
                            tenantUuid={tenantUuid}
                        />
                    )
                }
                mainRoute={TENANTS_ROUTE}
                viewActions={
                    viewTenants() ? (
                        <>
                            {watchTutorialButton}
                            {createViewActions()}
                        </>
                    ) : undefined
                }
                hideActions={watchTutorialButton}
                onHandleRefresh={onRefreshing}
                enableRefresh
            />
            <VideoModal
                isOpen={showVideoModal}
                hideModal={() => setShowVideoModal(false)}
                linkUrl={TutorialVideoLink.TENANTS}
            />
            <ErrorModal
                hide={() => setErrorModalVisible(false)}
                visible={errorModalVisible}
                title={t("Common.error")}
                description={t("CustomersTable.failedToFetchLicenses")}
            />
        </>
    );
}
